import {Component, HostListener, OnInit} from '@angular/core';
import {NavigationEnd, PRIMARY_OUTLET, Router} from '@angular/router';
import {
  UserService,
  AuthenticationService,
  CampaignService,
  AlertService,
  GuiService,
  AccountService, StorageService, LoaderService, ColorSwatchService
} from '../_services';
import {User, Account, Campaign, Pager, isCampaignSection, Image} from '../_models';
import {Form} from '../_forms'
import {Location} from '@angular/common';
import {takeUntil} from "rxjs/operators";
import { SideNavService } from '../_services/sidenav.service';
import {NgxSmartModalService} from 'ngx-smart-modal';


@Component({
  moduleId: module.id.toString(),
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent extends Form implements OnInit {
  currentUser: User;
  showAddButton: boolean = false;
  isSidebarExpanded: boolean = false;
  isHybridCampaign: boolean = false;
  showSideNavToggle: boolean = true;
  selectedAccount: Account;
  campaigns: Campaign[] = [];
  currentCampaign: Campaign = null;
  isCampaignDropdownOpen: boolean = false;
  isCampaignSection: boolean = false;
  currentSectionTitle: string = '';
  adminOnlyTag: string = null;
  isHomePage: boolean = false;
  showBackButton: boolean = true;

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    private authService: AuthenticationService,
    private campaignService: CampaignService,
    private userService: UserService,
    private guiService: GuiService,
    private sideNavService: SideNavService,
    protected accountService: AccountService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected storageService: StorageService,
    protected colorSwatchService: ColorSwatchService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.router.events.subscribe(data => {
      if (data instanceof NavigationEnd) {
        this.isHybridCampaign = data.url === '/hybrid-campaigns';
        this.showAddButton = this.isHybridCampaign || (data.url === '/campaigns');
        this.isCampaignSection = isCampaignSection(data.url);
        this.updateCurrentSectionTitle();
        this.updateIsHomePage();
        this.updateBackButton();
      }
    });

    this.userService.getCurrent().pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: User) => {
          this.currentUser = user;
          this.selectedAccount = user.account;
        },
        error => {
          this.handleError(error);
        }
      );

    this.guiService.getGuiDataObs().subscribe(data => {

      if ('title' in data) {
        this.title = data.title;
      }
      if ('sectionTitle' in data) {
        this.currentSectionTitle = data.sectionTitle;
      }
    })
    this.sideNavService.getSideNavObs().subscribe(data => {
      if ('isSideNavExpanded' in data) {
        this.isSidebarExpanded = data.isSideNavExpanded;
      }

      if ('showSideNavToggle' in data) {
        this.showSideNavToggle = data.showSideNavToggle
      }
    })

    this.sideNavService.setSideNavExpanded(this.isSidebarExpanded)
    this.sideNavService.setShowSideNavToggle(this.showSideNavToggle)
    this.accountService.selectedAccount$.subscribe((account: Account) => {
      if (account && account.account_changed) {
        this.fetchCampaigns(true);
        account.account_changed = false
      }
      this.selectedAccount = account;
      if (this.currentUser) {
        this.currentUser.account = account;
      }
    })

    this.currentCampaign = this.campaignService.getCurrentCampaign();
    this.colorSwatchService.loadBrandColors(this.currentCampaign);
    this.fetchCampaigns(false);
    this.updateCurrentSectionTitle();
    this.updateIsHomePage();
    this.updateBackButton();
    this.campaignService.currentCampaignUpdate$()
      .subscribe((campaign: Campaign) => {
        if (campaign && this.currentCampaign) {
          if (campaign.portal_creation_failed) {
            this.campaigns = this.campaigns.filter((item: Campaign) => item.id !== campaign.id);

            if (this.campaigns.length > 0) {
              this.selectCampaign(this.campaigns[0]);
            } else {
              this.campaignService.removeCurrentCampaign();
            }
          } else {
            this.currentCampaign.name = campaign.name
            this.currentCampaign.display_name = campaign.display_name
            this.currentCampaign.favicon = campaign.favicon

            this.campaigns.forEach((item: Campaign) => {
              if (item.id === campaign.id) {
                item.name = campaign.name
                item.display_name = campaign.display_name
                item.favicon = campaign.favicon
              }
            })
          }
        }
      })
  }

  fetchCampaigns(accountChanged) {
    this.campaignService.list({is_hybrid: false})
      .subscribe((data: Pager) => {
        if (data) {
          this.campaigns = data.results;
          if (accountChanged || !this.currentCampaign) {
            this.currentCampaign = this.campaigns[0];
            this.selectCampaign(this.currentCampaign, true, false);
          }
        }
      }, error => {
        this.handleError(error);
      });
  }

  isLoggedIn() {
    return this.authService.isLoggedIn();
  }

  gotoAccountPage(page) {
    this.campaignService.hideMenu();
    let path = null;

    switch (page) {
      case 'subscription':
        path = ['/user', 'subscribe', 'edit'];
        break;

      case 'payment':
        path = ['/payment', 'sources'];
        break;

      case 'plan':
        path = ['/plan', 'edit', this.currentUser.account.plan.id];
        break;

      case 'resolve-plan':
        path = ['/resolve-plan', 'edit', this.currentUser.account.resolve_plan.id];
        break;

      default:
        path = ['/' + page];
        break;
    }

    if (path) {
      this.router.navigate(path);
    }
  }


  logout() {
    this.authService.logout();
    this.router.navigate(['/user', 'login']);
  }

  get currentUserFullName() {
    return this.currentUser ? this.currentUser.first_name + ' ' + this.currentUser.last_name : '';
  }

  get welcomeTitle() {
    let title = 'Welcome back';
    if (this.currentUser && this.currentUser.first_name) {
      title += ', ' + this.currentUser.first_name;
    }
    return title;
  }

  getCampaignIcon(campaign: Campaign) {
    return campaign && campaign.favicon ? (campaign.favicon as Image).file : null
  }

  getIconPlaceholder(campaign: Campaign) {
    return campaign ? campaign.display_name[0] : ''
  }

  selectCampaign(campaign: Campaign, fetchDetailed: boolean = true, reload: boolean = true) {
    this.currentCampaign = campaign;
    this.storageService.set('selectedStepProduct', null);

    if (fetchDetailed) {
      this.loaderService.show();
      this.campaignService.get(campaign.id)
        .subscribe((campaign: Campaign) => {
          this.campaignService.selectCurrentCampaign(campaign);
          this.colorSwatchService.loadBrandColors(campaign);
          this.loaderService.hide();

          if (reload) {
            window.location.reload();
          }
        }, error => {
          this.loaderService.hide();
          this.handleError(error);
        });
    } else {
      this.campaignService.selectCurrentCampaign(campaign);
    }
  }

  toggleDropdown(event) {
    event.stopPropagation()
    this.isCampaignDropdownOpen = !this.isCampaignDropdownOpen
  }

  @HostListener('document:click', ['$event'])
  closeDropdown(event: Event) {
    if (this.isCampaignDropdownOpen) {
      this.isCampaignDropdownOpen = false;
    }
  }

  openCampaignCreationPopup() {
    this.modalService.getModal('campaignSavePopup').open();
  }

  onCampaignCreated(campaign: Campaign) {
    this.modalService.getModal('campaignSavePopup').close();
    this.campaigns.push(campaign);
    this.selectCampaign(campaign, false);
    this.router.navigate(['/campaign', 'general'], { replaceUrl: true });
    this.alertService.success('Brand created successfully');
  }

  openIntercomMessenger() {
    if (window && (window as any).Intercom) {
      (window as any).Intercom('show');
    }
  }

  updateCurrentSectionTitle() {
    const title = this.findRouteData('title');
    this.currentSectionTitle = title ? title : 'Dashboard';
    this.adminOnlyTag = this.findRouteData('adminOnlyTag');
  }

  // A product's first page sets hideBackButton; people return Home through the sidebar instead.
  updateBackButton() {
    this.showBackButton = !this.findRouteData('hideBackButton');
  }

  // The deepest route that sets the key wins.
  findRouteData(key: string) {
    let value = null;
    let currentRoute = this.router.routerState.root;

    while (currentRoute) {
      if (currentRoute.snapshot.data && currentRoute.snapshot.data[key]) {
        value = currentRoute.snapshot.data[key];
      }
      currentRoute = currentRoute.firstChild;
    }

    return value;
  }

  // Home is the root URL, so its tree has no primary-outlet segments.
  updateIsHomePage() {
    const urlTree = this.router.parseUrl(this.router.url);
    this.isHomePage = !urlTree.root.children[PRIMARY_OUTLET];
  }
}
